import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Stack,
  Typography,
  Box,
} from '@mui/material';
import { IoPencilOutline } from 'react-icons/io5';
import { Class } from 'src/types/class.types';
import { CohortWithDetails, UpdateCohortDTO } from 'src/types/cohort.types';

interface EditCohortModalProps {
  open: boolean;
  onClose: () => void;
  cohort: CohortWithDetails | null;
  classes: Class[];
  onSubmit: (cohortId: string, dto: UpdateCohortDTO) => Promise<void>;
  isSubmitting?: boolean;
}

export function EditCohortModal({
  open,
  onClose,
  cohort,
  classes,
  onSubmit,
  isSubmitting = false,
}: EditCohortModalProps) {
  const [classId, setClassId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (cohort) {
      setClassId(cohort.class_id || '');
      setName(cohort.name || '');
      setDescription(cohort.description || '');
      setError('');
    }
  }, [cohort, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cohort) return;
    if (!classId) {
      setError('Please select a parent Grade Level / Course');
      return;
    }
    if (!name.trim()) {
      setError('Please enter a cohort section name');
      return;
    }

    try {
      await onSubmit(cohort.id, {
        class_id: classId,
        name: name.trim(),
        description: description.trim() || undefined,
      });
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to update cohort');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: '#FEF3C7',
                color: '#D97706',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <IoPencilOutline size={22} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Edit Cohort Details
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Update cohort section name, description, or parent grade assignment
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2 }}>
          <Stack spacing={2.5}>
            {error && (
              <Typography variant="caption" color="error" fontWeight={600}>
                {error}
              </Typography>
            )}

            <FormControl fullWidth size="small" required>
              <InputLabel id="edit-parent-grade-label">Parent Grade Level / Course</InputLabel>
              <Select
                labelId="edit-parent-grade-label"
                value={classId}
                label="Parent Grade Level / Course"
                onChange={(e) => setClassId(e.target.value as string)}
                sx={{ borderRadius: 1.5 }}
              >
                {classes.map((cls) => (
                  <MenuItem key={cls.id} value={cls.id}>
                    {cls.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Cohort Section Name"
              placeholder="e.g. 7A, 7-Harvard Scholars"
              fullWidth
              size="small"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              InputProps={{ sx: { borderRadius: 1.5 } }}
            />

            <TextField
              label="Description (Optional)"
              placeholder="e.g. Homeroom advisory section for period 1"
              fullWidth
              size="small"
              multiline
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              InputProps={{ sx: { borderRadius: 1.5 } }}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={isSubmitting} sx={{ color: '#64748B', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              bgcolor: '#EDB42E',
              color: '#000',
              fontWeight: 700,
              px: 3,
              '&:hover': { bgcolor: '#d9a224' },
            }}
          >
            {isSubmitting ? <CircularProgress size={20} sx={{ color: '#000' }} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
